import { useCallback, useEffect, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { catalogFetch } from './catalogApi.js';

const DEFAULT_BASIC_PRICE = 0.5;

function BuyOrderForm({
  onSubmit,
  disabled,
  authToken = "",
  selectedMarketId = "",
  availableMarkets = null,
  showMarketSelector = true,
  renderMode = "tabs",
  initialOutcome = "",
  onSubmitSuccess,
}) {
  const [market, setMarket] = useState("");
  const [internalMarkets, setInternalMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [marketsError, setMarketsError] = useState("");
  const [price, setPrice] = useState("0.00");
  const [qty, setQty] = useState("1");
  const [orderType, setOrderType] = useState("");
  const [bidMode, setBidMode] = useState("basic");
  const [customPrices, setCustomPrices] = useState({});
  const [quotesByMarket, setQuotesByMarket] = useState({});
  const markets = Array.isArray(availableMarkets) ? availableMarkets : internalMarkets;
  const hasMultipleMarkets = markets.length > 1;

  function handleOptionSelection(order_type, price) {
    setOrderType(order_type);
    setPrice(String(price));
  }
  function handleBasicSelection(marketId, outcome, selectedPrice) {
    setMarket(marketId);
    handleOptionSelection(outcome, selectedPrice);
  }
  const getMarketJson = useCallback(
    (marketId) => markets.find((marketItem) => marketItem.market_id === marketId),
    [markets]
  );

  function toProbability(rawPrice) {
    const numeric = Number(rawPrice);
    if (!Number.isFinite(numeric)) {
      return DEFAULT_BASIC_PRICE;
    }
    return numeric > 1 ? numeric / 100 : numeric;
  }

  const getPriceRows = useCallback((marketId) => getMarketJson(marketId)?.prices || [], [getMarketJson]);

  const getYesPrice = useCallback((marketId) => {
    const marketJson = getMarketJson(marketId) || {};
    const priceRows = getPriceRows(marketId);
    const yesJson = priceRows.find(
      (row) => String(row.outcome || "").toUpperCase() === "YES"
    );
    if (yesJson?.price != null) {
      return toProbability(yesJson.price);
    }

    const binaryJson = priceRows.find(
      (row) => String(row.outcome || "").toUpperCase() === "BINARY"
    );
    if (binaryJson?.price != null) {
      return toProbability(binaryJson.price);
    }

    if (marketJson.yes_price != null) {
      return toProbability(marketJson.yes_price);
    }

    return DEFAULT_BASIC_PRICE;
  }, [getMarketJson, getPriceRows]);

  function handleCustomPriceChange(contractKey, value) {
    setCustomPrices((prev) => ({ ...prev, [contractKey]: value }));
    if (orderType === contractKey) {
      setPrice(value);
    }
  }

  function handleProSelection(contractKey) {
    const nextPrice = customPrices[contractKey] || "0.50";
    handleOptionSelection(contractKey, nextPrice);
  }

  const getFallbackBasicPrice = useCallback((marketId, targetOutcome) => {
    const yesPrice = getYesPrice(marketId);
    if (targetOutcome === "YES") {
      return yesPrice;
    }
    return 1 - yesPrice;
  }, [getYesPrice]);

  const getBasicPrice = useCallback((marketId, targetOutcome) => {
    const quote = quotesByMarket[marketId]?.[targetOutcome];
    const displayPrice = Number(quote?.display_price);
    if (Number.isFinite(displayPrice) && displayPrice > 0 && displayPrice < 1) {
      return displayPrice;
    }
    return getFallbackBasicPrice(marketId, targetOutcome);
  }, [getFallbackBasicPrice, quotesByMarket]);

  const getResolvedPriceRows = useCallback((marketId) => {
    const rows = getPriceRows(marketId);
    if (rows.length > 0) {
      return rows;
    }

    return [{
      outcome: "BINARY",
      yesPrice: getBasicPrice(marketId, "YES"),
      noPrice: getBasicPrice(marketId, "NO"),
    }];
  }, [getBasicPrice, getPriceRows]);

  useEffect(() => {
    if (selectedMarketId) {
      setMarket(selectedMarketId);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    if (!selectedMarketId || !initialOutcome) {
      return;
    }

    const normalizedOutcome = String(initialOutcome).toUpperCase();
    if (normalizedOutcome !== "YES" && normalizedOutcome !== "NO") {
      return;
    }

    setMarket(selectedMarketId);
    setOrderType(normalizedOutcome);
    setPrice(String(getBasicPrice(selectedMarketId, normalizedOutcome)));
  }, [selectedMarketId, initialOutcome, quotesByMarket, markets, getBasicPrice]);

  useEffect(() => {
    if (!market || (orderType !== "YES" && orderType !== "NO")) {
      return;
    }

    setPrice(String(getBasicPrice(market, orderType)));
  }, [market, orderType, getBasicPrice]);

  useEffect(() => {
    let cancelled = false;

    const loadQuotes = async () => {
      const targetMarketIds = showMarketSelector
        ? (market ? [market] : [])
        : markets.map((item) => item.market_id).filter(Boolean);

      if (targetMarketIds.length === 0) {
        setQuotesByMarket({});
        return;
      }
      if (!authToken) {
        setQuotesByMarket({});
        return;
      }

      try {
        const responses = await Promise.all(
          targetMarketIds.map(async (marketId) => {
            const [yesQuote, noQuote] = await Promise.all([
              catalogFetch(`/markets/${encodeURIComponent(marketId)}/quote?outcome=YES`, authToken),
              catalogFetch(`/markets/${encodeURIComponent(marketId)}/quote?outcome=NO`, authToken),
            ]);
            return { marketId, yesQuote, noQuote };
          })
        );

        if (!cancelled) {
          const nextQuotesByMarket = {};
          responses.forEach((entry) => {
            nextQuotesByMarket[entry.marketId] = {
              YES: entry.yesQuote,
              NO: entry.noQuote,
            };
          });
          setQuotesByMarket(nextQuotesByMarket);
        }
      } catch (_err) {
        if (!cancelled) {
          setQuotesByMarket({});
        }
      }
    };

    loadQuotes();
    return () => {
      cancelled = true;
    };
  }, [market, markets, showMarketSelector, authToken]);

  useEffect(() => {
    if (Array.isArray(availableMarkets)) {
      setMarketsError("");
      setMarketsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadMarkets = async () => {
      if (!authToken) {
        setMarketsError("");
        setInternalMarkets([]);
        return;
      }

      setMarketsLoading(true);
      setMarketsError("");
      try {
        const body = await catalogFetch("/markets", authToken);

        const items = Array.isArray(body.items) ? body.items : [];
        const validItems = items.filter((item) => item && item.market_id);
        if (!cancelled) {
          setInternalMarkets(validItems);
          setMarket((current) => {
            if (current && validItems.some((item) => item.market_id === current)) {
              return current;
            }
            return validItems[0]?.market_id || "";
          });
        }
      } catch (err) {
        if (!cancelled) {
          setMarketsError(err.message || "Failed to load markets");
        }
      } finally {
        if (!cancelled) {
          setMarketsLoading(false);
        }
      }
    };

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [availableMarkets, authToken]);

  const selectedMarket = market
    ? markets.find((item) => item.market_id === market)
    : null;

  const selectedContractLabel = useMemo(() => {
    if (!orderType) {
      return "None selected";
    }
    if (!orderType.includes("|")) {
      return orderType;
    }
    const [, side] = orderType.split("|");
    return `${side}`;
  }, [orderType]);

  const payload = useMemo(() => {
    const normalizedPrice = Number(price);
    const ticks = Math.round(normalizedPrice * 100);
    const normalizedOutcome = orderType
      ? (orderType.includes("|") ? orderType.split("|")[1] : orderType)
      : "";
    return {
      market_id: market,
      outcome: String(normalizedOutcome).toUpperCase(),
      limit_price: ticks,
      qty: Number.parseInt(qty, 10),
    };
  }, [market, orderType, price, qty]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericPrice = Number(price);
    const numericQty = Number(qty);
    if (!(numericPrice > 0 && numericPrice < 1)) {
      alert("Price must be between 0 and 1");
      return;
    }
    if (!Number.isInteger(numericQty) || numericQty <= 0) {
      alert("Quantity must be a positive integer");
      return;
    }
    if (!market) {
      alert("Select a market");
      return;
    }
    if (!orderType) {
      alert("Select a contract");
      return;
    }
    await onSubmit(payload);
    onSubmitSuccess?.();
  };

  const isBasicOnly = renderMode === "basic_only";
  const isProOnly = renderMode === "pro_only";
  const formClassName = `buy-order-form${isProOnly ? " buy-order-form-pro" : ""}`;

  const renderBasicModeContent = () => {
    if (!market) {
      return null;
    }

    return (
      <div className="d-flex justify-content-center gap-3 mb-3 flex-wrap">
        <Button
          type="button"
          size="lg"
          className={`px-4 fw-semibold buy-order-action buy-order-action-yes${
            orderType === "YES" ? " is-selected" : ""
          }`}
          variant={orderType === "YES" ? "success" : "outline-success"}
          onClick={() => handleOptionSelection("YES", getBasicPrice(market, "YES"))}
        >
          YES ${getBasicPrice(market, "YES").toFixed(2)}
        </Button>
        <Button
          type="button"
          size="lg"
          className={`px-4 fw-semibold buy-order-action buy-order-action-no${
            orderType === "NO" ? " is-selected" : ""
          }`}
          variant={orderType === "NO" ? "danger" : "outline-danger"}
          onClick={() => handleOptionSelection("NO", getBasicPrice(market, "NO"))}
        >
          NO ${getBasicPrice(market, "NO").toFixed(2)}
        </Button>
      </div>
    );
  };

  const renderCategoryBasicModeContent = () => {
    if (!hasMultipleMarkets) {
      return renderBasicModeContent();
    }

    return (
      <div className="d-flex flex-column gap-2 mb-3">
        {markets.map((marketItem) => {
          const marketId = marketItem.market_id;
          const yesPrice = getBasicPrice(marketId, "YES");
          const noPrice = getBasicPrice(marketId, "NO");
          return (
            <div
              key={marketId}
              className="buy-order-market-row d-flex align-items-center justify-content-between gap-2"
            >
              <div className="fw-semibold text-start buy-order-market-label">
                {marketItem.option_label || marketItem.title || marketId}
              </div>
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  className={`buy-order-action buy-order-action-yes${
                    market === marketId && orderType === "YES" ? " is-selected" : ""
                  }`}
                  variant={
                    market === marketId && orderType === "YES"
                      ? "success"
                      : "outline-success"
                  }
                  onClick={() => handleBasicSelection(marketId, "YES", yesPrice)}
                  disabled={disabled}
                >
                  YES ${yesPrice.toFixed(2)}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className={`buy-order-action buy-order-action-no${
                    market === marketId && orderType === "NO" ? " is-selected" : ""
                  }`}
                  variant={
                    market === marketId && orderType === "NO"
                      ? "danger"
                      : "outline-danger"
                  }
                  onClick={() => handleBasicSelection(marketId, "NO", noPrice)}
                  disabled={disabled}
                >
                  NO ${noPrice.toFixed(2)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderProModeContent = () => (
    <>
      <Alert variant="secondary" className="mb-3 buy-order-pro-banner">
        Enter a custom price, then click the side you want to bid. Selected:{" "}
        <strong>
        {selectedMarket?.option_label || selectedMarket?.title || selectedMarket?.market_id || "None selected"}
        {" "} {selectedContractLabel} at {" "}
        {Number(price || 0).toFixed(2)}
        </strong>
      </Alert>
      {!showMarketSelector && hasMultipleMarkets && (
        <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
          {markets.map((marketItem) => (
            <Button
              key={marketItem.market_id}
              type="button"
              size="sm"
              className={`buy-order-market-pill${
                market === marketItem.market_id ? " is-selected" : ""
              }`}
              variant={market === marketItem.market_id ? "primary" : "outline-primary"}
              onClick={() => setMarket(marketItem.market_id)}
              disabled={disabled}
            >
              {marketItem.option_label || marketItem.title || marketItem.market_id}
            </Button>
          ))}
        </div>
      )}
      {market && getResolvedPriceRows(market).map((outcomeRow) => {
        const outcomeKey = String(outcomeRow.outcome || "");
        const yesKey = `${outcomeKey}|YES`;
        const noKey = `${outcomeKey}|NO`;
        const defaultYesValue = toProbability(
          outcomeRow.yesPrice != null ? outcomeRow.yesPrice : outcomeRow.price
        ).toFixed(2);
        const defaultNoValue = toProbability(
          outcomeRow.noPrice != null ? outcomeRow.noPrice : (1 - toProbability(outcomeRow.price))
        ).toFixed(2);
        const yesValue =
          customPrices[yesKey]
          ?? (orderType === yesKey ? String(price) : defaultYesValue);
        const noValue =
          customPrices[noKey]
          ?? (orderType === noKey ? String(price) : defaultNoValue);
        return (
          <Form.Group className="mb-3 border rounded p-3 buy-order-contract-card" key={outcomeKey}>
            <Form.Label className="fw-semibold">{outcomeKey}</Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <span className="buy-order-side-label">YES</span>
              <Form.Control
                type="number"
                min="0.01"
                max="0.99"
                step="0.01"
                value={yesValue}
                onChange={(e) => handleCustomPriceChange(yesKey, e.target.value)}
                disabled={disabled}
              />
              <Button
                type="button"
                className={`buy-order-action buy-order-action-yes${
                  orderType === yesKey ? " is-selected" : ""
                }`}
                variant={orderType === yesKey ? "success" : "outline-success"}
                onClick={() => handleProSelection(yesKey)}
                disabled={disabled}
              >
                YES
              </Button>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className="buy-order-side-label">NO</span>
              <Form.Control
                type="number"
                min="0.01"
                max="0.99"
                step="0.01"
                value={noValue}
                onChange={(e) => handleCustomPriceChange(noKey, e.target.value)}
                disabled={disabled}
              />
              <Button
                type="button"
                className={`buy-order-action buy-order-action-no${
                  orderType === noKey ? " is-selected" : ""
                }`}
                variant={orderType === noKey ? "danger" : "outline-danger"}
                onClick={() => handleProSelection(noKey)}
                disabled={disabled}
              >
                NO
              </Button>
            </div>
          </Form.Group>
        );
      })}
    </>
  );

  const renderOrderFooter = () => (
    <>
      <Form.Group className="d-flex align-items-center justify-content-center gap-2 buy-order-qty-group">
        <Form.Label className="mb-0">Qty</Form.Label>
        <Form.Control
          type="number"
          min="1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="buy-order-qty-input"
        />
      </Form.Group>
      <Alert variant="light" className="mt-3 buy-order-summary">
        <div className="buy-order-summary-header">
          <div className="buy-order-summary-title">Trade Summary</div>
          <div className="buy-order-summary-caption">Review the order before submission.</div>
        </div>
        <div className="buy-order-summary-grid">
          <div className="buy-order-summary-row">
            <span className="buy-order-summary-label">Market</span>
            <span className="buy-order-summary-value">
              {selectedMarket?.option_label || selectedMarket?.title || selectedMarket?.market_id || "None selected"}
            </span>
          </div>
          <div className="buy-order-summary-row">
            <span className="buy-order-summary-label">Selected</span>
            <span className="buy-order-summary-value">{selectedContractLabel}</span>
          </div>
          <div className="buy-order-summary-row">
            <span className="buy-order-summary-label">Price</span>
            <span className="buy-order-summary-value">{Number(price || 0).toFixed(2)}</span>
          </div>
          <div className="buy-order-summary-row">
            <span className="buy-order-summary-label">Cost</span>
            <span className="buy-order-summary-value">
              {(Number(qty || 0) * Number(price || 0)).toFixed(2)}
            </span>
          </div>
          <div className="buy-order-summary-row">
            <span className="buy-order-summary-label">Payout</span>
            <span className="buy-order-summary-value">{Number(qty || 0)}</span>
          </div>
        </div>
      </Alert>
      <Button
        type="submit"
        className="buy-order-submit"
        disabled={disabled || marketsLoading || markets.length === 0 || !market}
      >
        Submit
      </Button>
    </>
  );

  return (
    <Form onSubmit={handleSubmit} noValidate className={formClassName}>
      {marketsError && (
        <Alert variant="warning">{marketsError}</Alert>
      )}
      {showMarketSelector && (
        <Form.Group className="mb-3">
          <Form.Label>Market</Form.Label>
          <Form.Select
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            disabled={disabled || marketsLoading || markets.length === 0}
          >
            {markets.length === 0 ? (
              <option value="">
                {marketsLoading ? "Loading markets..." : "No markets available"}
              </option>
            ) : (
              markets.map((item) => (
                <option key={item.market_id} value={item.market_id}>
                  {item.title || item.market_id}
                </option>
              ))
            )}
          </Form.Select>
        </Form.Group>
      )}
      {isBasicOnly ? (
        <>
          {showMarketSelector ? renderBasicModeContent() : renderCategoryBasicModeContent()}
          {renderOrderFooter()}
        </>
      ) : isProOnly ? (
        <>
          {renderProModeContent()}
          {renderOrderFooter()}
        </>
      ) : (
        <>
          <Tabs
            activeKey={bidMode}
            onSelect={(k) => setBidMode(k || "basic")}
            className="mb-3"
          >
            <Tab eventKey="basic" title="Basic">
              <div className="pt-3">
                {showMarketSelector ? renderBasicModeContent() : renderCategoryBasicModeContent()}
              </div>
            </Tab>
            <Tab eventKey="pro" title="Limit Orders">
              <div className="pt-3">
                {renderProModeContent()}
              </div>
            </Tab>
          </Tabs>
          {renderOrderFooter()}
        </>
      )}
    </Form>
  );
}

export default BuyOrderForm;
